import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../utils/api';

export default function Register() {
    const [formData, setFormData] = useState({
        email: '',
        full_name: '',
        role: 'investor',
        password: '',
        confirmPassword: '',
        organisation_name: '',
        organisation_type: '',
        country: '',
        intended_use: ''
    });
    const inputClass = "w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-2.5 text-white outline-none focus:border-emerald-500";
    const [roles, setRoles] = useState(['investor', 'expert']);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchRoles = async () => {
            try {
                const res = await api.get('/api/auth/roles');
                setRoles(res.data.allowed);
            } catch (err) { console.error("Failed to fetch roles"); }
        };
        fetchRoles();
    }, []);

    const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (formData.password !== formData.confirmPassword) {
            return setError('Passwords do not match');
        }
        if (formData.password.length < 8) {
            return setError('Password must be at least 8 characters');
        }

        setLoading(true);
        try {
            const payload = {
                email: formData.email,
                full_name: formData.full_name,
                role: formData.role,
                password: formData.password,
                organisation_name: formData.organisation_name,
                organisation_type: formData.organisation_type,
                country: formData.country,
                intended_use: formData.intended_use
            };
            await api.post('/api/auth/register', payload);
            setSuccess('Registration successful! Please wait for admin approval.');
            setTimeout(() => navigate('/login'), 3000);
        } catch (err) {
            setError(err.response?.data?.error || 'Registration failed');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-slate-900">
            <div className="glass p-8 rounded-3xl max-w-md w-full border border-white/10 shadow-2xl relative">
                <Link to="/" className="absolute top-4 left-4 text-slate-500 hover:text-emerald-400 transition-colors">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path></svg>
                </Link>
                <h1 className="text-3xl font-bold mb-2 bg-clip-text text-transparent bg-gradient-to-r from-emerald-400 to-cyan-400 mt-4">Join Platform</h1>
                <p className="text-slate-400 mb-8">Create your attestation account</p>

                {error && <div className="bg-rose-500/20 text-rose-400 p-3 rounded-xl mb-6 text-sm border border-rose-500/30">{error}</div>}
                {success && <div className="bg-emerald-500/20 text-emerald-400 p-3 rounded-xl mb-6 text-sm border border-emerald-500/30">{success}</div>}

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-400 mb-1">Full Name</label>
                        <input type="text" name="full_name" required className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-2.5 text-white outline-none focus:border-emerald-500" value={formData.full_name} onChange={handleChange} />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-400 mb-1">Email</label>
                        <input type="email" name="email" required className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-2.5 text-white outline-none focus:border-emerald-500" value={formData.email} onChange={handleChange} />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-400 mb-1">Requested Role</label>
                        <select name="role" className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-2.5 text-white outline-none focus:border-emerald-500 capitalize" value={formData.role} onChange={handleChange}>
                            {roles.map(r => <option key={r} value={r}>{r}</option>)}
                        </select>
                    </div>

                    {/* Organisation Details */}
                    <div className="pt-4 border-t border-white/5">
                        <p className="text-[10px] font-bold uppercase tracking-widest 
                                      text-slate-500 mb-4">
                            Organisation Details
                        </p>
                        
                        <div className="space-y-4">
                            <div>
                                <label className="block text-xs font-bold 
                                                  text-slate-400 mb-2 uppercase 
                                                  tracking-widest">
                                    Organisation Name
                                    <span className="text-slate-600 ml-1 
                                                     normal-case font-normal">
                                        (or individual name)
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    name="organisation_name"
                                    value={formData.organisation_name || ''}
                                    onChange={handleChange}
                                    placeholder="Company Ltd / Your Name"
                                    className={inputClass}
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-bold 
                                                  text-slate-400 mb-2 uppercase 
                                                  tracking-widest">
                                    Organisation Type
                                </label>
                                <select
                                    name="organisation_type"
                                    value={formData.organisation_type || ''}
                                    onChange={handleChange}
                                    className={inputClass}
                                >
                                    <option value="">Select type...</option>
                                    <option value="corporate">
                                        Corporate Entity
                                    </option>
                                    <option value="institutional">
                                        Institutional Investor / Fund
                                    </option>
                                    <option value="dfi">
                                        Development Finance Institution
                                    </option>
                                    <option value="project_developer">
                                        Project Developer
                                    </option>
                                    <option value="individual">
                                        Individual
                                    </option>
                                    <option value="government">
                                        Government / Regulatory Body
                                    </option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-xs font-bold 
                                                  text-slate-400 mb-2 uppercase 
                                                  tracking-widest">
                                    Country of Incorporation / Residence
                                </label>
                                <input
                                    type="text"
                                    name="country"
                                    value={formData.country || ''}
                                    onChange={handleChange}
                                    placeholder="e.g. Nepal, Singapore, Germany"
                                    className={inputClass}
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-bold 
                                                  text-slate-400 mb-2 uppercase 
                                                  tracking-widest">
                                    Intended Use
                                </label>
                                <select
                                    name="intended_use"
                                    value={formData.intended_use || ''}
                                    onChange={handleChange}
                                    className={inputClass}
                                >
                                    <option value="">Select intended use...</option>
                                    <option value="scope2_reporting">
                                        Scope 2 ESG / Sustainability Reporting
                                    </option>
                                    <option value="project_development">
                                        Renewable Energy Project Development
                                    </option>
                                    <option value="regulatory_oversight">
                                        Regulatory Oversight
                                    </option>
                                    <option value="platform_verification">
                                        Technical Verification / Committee Role
                                    </option>
                                    <option value="research">
                                        Research / Due Diligence
                                    </option>
                                </select>
                            </div>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-slate-400 mb-1">Password</label>
                        <input type="password" name="password" required className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-2.5 text-white outline-none focus:border-emerald-500" value={formData.password} onChange={handleChange} />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-400 mb-1">Confirm Password</label>
                        <input type="password" name="confirmPassword" required className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-2.5 text-white outline-none focus:border-emerald-500" value={formData.confirmPassword} onChange={handleChange} />
                    </div>
                    <button type="submit" disabled={loading} className="w-full py-3 mt-4 bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-400 hover:to-cyan-400 text-white font-bold rounded-xl shadow-lg hover:shadow-emerald-500/20 transition-all active:scale-95 disabled:opacity-50 hover:scale-[1.02]">
                        {loading ? 'Processing...' : 'Register'}
                    </button>
                </form>

                <p className="mt-6 text-center text-slate-500 text-sm">
                    Already have an account? <Link to="/login" className="text-emerald-400 hover:text-emerald-300 font-medium">Login</Link>
                </p>
            </div>
        </div>
    );
}
